import { api } from './client';

/** One line as printed on the inkooporder PDF. */
export interface InkooporderLine {
  articleNumber: string;
  supplierCode: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
}

/** Personal data of a private individual — shown on screen, never persisted here. */
export interface DeliveryAddress {
  name: string;
  street: string;
  postcode: string;
  city: string;
  country: string;
  phone: string;
  email: string;
}

export interface InkooporderDocument {
  orderNumber: string;
  orderDigits: string;
  orderDate: string | null;
  deliveryDate: string | null;
  departmentCode: string | null;
  paymentTerms: string | null;
  shippingMethod: string | null;
  delivery: DeliveryAddress;
  lines: InkooporderLine[];
  totalExcl: number;
  totalVat: number | null;
  totalIncl: number | null;
}

/** A PDF line matched to the Odoo variant it will become. */
export interface DraftLine {
  articleNumber: string;
  variantId: number;
  variantCode: string;
  variantName: string;
  quantity: number;
  /** What goes to Odoo — Easykit's price, or the shared tier where that applies. */
  unitPrice: number;
  /** Easykit's own price, so the change stays visible. */
  documentUnitPrice: number;
  lineTotal: number;
}

export interface DraftOrderIntent {
  clientOrderRef: string;
  orderDigits: string;
  orderDate: string | null;
  deliveryDate: string | null;
  branchCode: string | null;
  delivery: DeliveryAddress;
  lines: DraftLine[];
  /** The document's own total excl. VAT. */
  totalExcl: number;
  /** What the order comes to after the shared-tier rule. */
  orderTotalExcl: number;
}

export type FindingLevel = 'refuse' | 'warn' | 'suggest' | 'applied';

export interface Finding {
  level: FindingLevel;
  code: string;
  message: string;
  articleNumber?: string;
}

/** An order that already carries this reference, with a way into Odoo. */
export interface ExistingOrder {
  id: number;
  name: string;
  state: string;
  url: string | null;
}

export interface ReviewContext {
  branchName: string | null;
  existingShippingName: string | null;
  resumableOrderId: number | null;
  existingOrders: ExistingOrder[];
}

/** A document that travelled with the order in the same message. */
export interface InkooporderAttachment {
  name: string;
  bytes: number;
}

/** Where the upload came from, as the message itself says. */
export interface MailDetails {
  subject: string;
  from: string | null;
  sentAt: string | null;
  filename: string;
}

/** What the dossier would file, named the way it will be named. */
export interface DossierPlan {
  enabled: boolean;
  root: string;
  year: string;
  /** Stands in for the order number Odoo has not issued yet. */
  orderPlaceholder: string;
  subfolders: string[];
  messageFileName: string | null;
  /** Filed as `{mapnaam} {suffix}`. */
  prefixedFiles: string[];
}

export interface InkooporderParseResult {
  document: InkooporderDocument;
  /** Null when a refusal stands — there is then nothing we would import. */
  intent: DraftOrderIntent | null;
  findings: Finding[];
  context: ReviewContext;
  /** What was dropped: the order on its own, or the mail it arrived in. */
  source: 'pdf' | 'email';
  /** The drawings and other documents in that mail, names and sizes only. */
  documents: InkooporderAttachment[];
  /** Set for a mail: the order itself, so the preview can still show it. */
  orderPdfBase64: string | null;
  /** Null when a bare PDF was dropped. */
  mail: MailDetails | null;
  plan: DossierPlan;
}

/** What was filed in SharePoint alongside the order. */
export interface DossierResult {
  /** False when the dossier is switched off; nothing was attempted. */
  enabled: boolean;
  folderPath: string | null;
  folderUrl: string | null;
  steps: string[];
  errors: string[];
}

export interface InkooporderCommitResult {
  saleOrderId: number;
  saleOrderName: string;
  /** Straight into the order in Odoo, to check and confirm it. */
  saleOrderUrl: string | null;
  shippingPartnerId: number | null;
  branchPartnerName: string | null;
  /** Dutch, in the order they happened. */
  steps: string[];
  findings: Finding[];
  /** The SharePoint dossier. Its failures never fail the order. */
  dossier: DossierResult;
}

export async function parseInkooporder(file: File): Promise<InkooporderParseResult> {
  const fd = new FormData();
  fd.append('file', file);
  return api<InkooporderParseResult>('/api/easykit/inkooporder/parse', { method: 'POST', body: fd });
}

/**
 * Create the draft order. The whole document goes back, corrections and all —
 * the server re-runs every rule on it rather than trusting the review screen.
 */
export async function commitInkooporder(
  file: File,
  document: InkooporderDocument,
  resumeOrderId?: number | null,
): Promise<InkooporderCommitResult> {
  const fd = new FormData();
  fd.append('document', JSON.stringify(document));
  if (resumeOrderId != null) fd.append('resumeOrderId', String(resumeOrderId));
  fd.append('file', file);
  return api<InkooporderCommitResult>('/api/easykit/inkooporder/commit', { method: 'POST', body: fd });
}
